import React, { memo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { ReactComponent as DriverOptInIllustration } from 'apps/fleet-planner/components/illustrations/DriverOptInIllustration.svg'
import { FP_FAQ_URL_DRIVER_LED_STATUS } from 'apps/fleet-planner/config'
import { useFMSAnalytics } from 'apps/fleet-planner/hooks'
import { IDriver } from 'apps/fleet-planner/types'
import { fetchLoadDetails } from 'apps/fleet-planner/utils/actions/loadDetails'
import { updateDriver } from 'apps/fleet-planner/utils/api'
import { ConfirmationModal, ToastState } from 'transfix-ui'
import { DriverSettingsModal } from './DriverSettingsModal'

interface IOptInModalProps {
  isOpen?: boolean
  drivers: IDriver[]
  onClose: () => void
  onSuccess: (toastState: Omit<ToastState, 'toastOpen'>) => void
  id?: string
}

export const DriverOptInModal = memo<IOptInModalProps>(
  ({ isOpen, onClose, drivers = [], onSuccess, id }) => {
    const { trackEvent } = useFMSAnalytics()
    const dispatch = useDispatch()
    const [driver1, driver2] = drivers || []
    const [apiError, setApiError] = useState<string | undefined>('')
    const [isConfirmDriverModaOpen, setIsConfirmDriverModalOpen] = useState(false)

    const submitHandler = (toastState: Omit<ToastState, 'toastOpen'>) => {
      onSuccess(toastState)
      trackEvent('Driver Led Status', 'DRIVER_LED_STATUS_DRIVER_CELL_CONFIRMED')
      trackEvent('Driver Led Status', 'DRIVER_LED_STATUS_LOADS_MODAL_UPDATES_ON')
      onClose()
    }

    const dismissHandler = async () => {
      trackEvent('Driver Led Status', 'DRIVER_OPT_IN_MODAL_DISMISSED')
      const requestOne = {
        ...driver1,
        settings: {
          smsStatusUpdatesEnabled: driver1.settings?.smsStatusUpdatesEnabled ?? false,
          suppressSmsStatusUpdatesOptIn: true,
        },
      }
      const requestTwo = {
        ...driver2,
        settings: {
          smsStatusUpdatesEnabled: driver2?.settings?.smsStatusUpdatesEnabled ?? false,
          suppressSmsStatusUpdatesOptIn: true,
        },
      }

      const driverRequests = []
      if (driver1?.id) driverRequests.push(updateDriver(driver1.id, requestOne))
      if (driver2?.id) driverRequests.push(updateDriver(driver2.id, requestTwo))
      try {
        await Promise.all(driverRequests)
        // If modal is open/id is present
        // Fetch load details in order to display updated smsStatusUpdatesEnabled value.
        if (id) dispatch(fetchLoadDetails(id))
      } catch (e) {
        setApiError(e)
      }
      onClose()
    }

    const closeConfirmationModalHandler = () => setIsConfirmDriverModalOpen(false)

    const openConfirmationModalHandler = () => {
      trackEvent('Driver Led Status', 'DRIVER_OPT_IN_MODAL_OPENED')
      setIsConfirmDriverModalOpen(true)
    }

    const getDriverNames = (): string => {
      if (!drivers?.length) return 'your driver(s)'
      return drivers.map(d => d.firstName).join(' & ')
    }

    const modalTitle = `Allow Transfix FMS to ask ${getDriverNames()} for updates?`

    return (
      <ConfirmationModal
        size={6}
        title={modalTitle}
        titleClass='tc'
        titleSize={2}
        open={isOpen}
        className='fp-driver-opt-in-modal'
        error={apiError}
        actions={[
          {
            label: 'No Thanks',
            type: 'dismiss',
            action: dismissHandler,
          },
          {
            label: 'Yes, Ask Driver For Status Updates',
            type: 'confirm',
            action: openConfirmationModalHandler,
          },
        ]}
        onClose={onClose}>
        <div className='tc'>
          <div className='body-medium mb2'>
            Transfix Carrier App will text your driver timely reminders to update their status.
            You’ll receive an email notification when they arrive and depart each pickup and
            dropoff. <a href={FP_FAQ_URL_DRIVER_LED_STATUS}>Learn More</a>
          </div>
          <DriverOptInIllustration />
        </div>
        <DriverSettingsModal
          onSuccess={submitHandler}
          onClose={closeConfirmationModalHandler}
          drivers={drivers}
          isOpen={isConfirmDriverModaOpen}
        />
      </ConfirmationModal>
    )
  }
)
